import isEmpty from "lodash-es/isEmpty";

import { getFromLocalStorage, hasLocalStorage, removeFromLocalStorage, saveToLocalStorage } from "@/utils/localStorage";
import { addToQueryString, getFromQueryString } from "@/utils/url";
import { Middleware, ResponseContext } from "@/api";

export class AppVersionMiddleware implements Middleware {
  private readonly APP_VERSION_RELOAD_TRIGGERED_STORAGE_KEY = "ls-avrt";
  private isEnabled = false;
  private isReloading = false;
  private readonly currentMajorVersion: number | undefined;
  private hasBeenReloaded = false;

  constructor(private readonly currentAppVersion: string | undefined) {
    if (currentAppVersion != null && !isEmpty(currentAppVersion)) {
      this.currentMajorVersion = this.getMajorVersionNumber(currentAppVersion);
      this.hasBeenReloaded = !!this.getTriggeredFlag();

      if (this.hasBeenReloaded) {
        setTimeout(() => {
          this.isEnabled = true;
          this.hasBeenReloaded = false;
          this.clearTriggeredFlag();
        }, 1800000);
      } else {
        this.isEnabled = true;
      }
    }
  }

  async post(context: ResponseContext): Promise<Response | void> {
    if (!this.isEnabled || this.currentMajorVersion == null || this.isReloading || this.hasBeenReloaded) {
      return;
    }

    const apiVersion = context.response.headers.get("X-App-Version");
    if (apiVersion == null) {
      return;
    }

    const apiMajorVersion = this.getMajorVersionNumber(apiVersion);
    if (apiMajorVersion !== this.currentMajorVersion) {
      this.scheduleReload();
    }
  }

  private getMajorVersionNumber(version: string): number {
    const majorVersion = parseInt(version.split(".")[0]);
    if (isNaN(majorVersion)) {
      throw new Error(`Invalid version format: ${version}`);
    }
    return majorVersion;
  }

  private scheduleReload(): void {
    console.warn(
      `API version mismatch: App version ${this.currentAppVersion} and API version detected. Scheduling hard reload...`,
    );
    this.isReloading = true;

    this.saveTriggeredFlag();

    setTimeout(() => {
      if (window.location.hash) {
        window.location.href = window.location.href.replace(window.location.hash, "");
      }
      window.location.reload();
    }, 0);
  }

  private saveTriggeredFlag() {
    if (hasLocalStorage()) {
      saveToLocalStorage(this.APP_VERSION_RELOAD_TRIGGERED_STORAGE_KEY, "true");
    } else {
      // Fallback to using query string when localStorage is not available
      addToQueryString(this.APP_VERSION_RELOAD_TRIGGERED_STORAGE_KEY, "true");
    }
  }

  private getTriggeredFlag() {
    if (hasLocalStorage()) {
      return getFromLocalStorage(this.APP_VERSION_RELOAD_TRIGGERED_STORAGE_KEY);
    }

    return getFromQueryString(this.APP_VERSION_RELOAD_TRIGGERED_STORAGE_KEY);
  }

  private clearTriggeredFlag() {
    if (hasLocalStorage()) {
      removeFromLocalStorage(this.APP_VERSION_RELOAD_TRIGGERED_STORAGE_KEY);
    } else {
      getFromQueryString(this.APP_VERSION_RELOAD_TRIGGERED_STORAGE_KEY, true);
    }
  }
}
